import { createContext } from '@direct-frontend/modules-context';

/**
 * Опции конфигурации для поведения мемоизации.
 *
 * @typeParam T - Тип закешированного значения
 * @typeParam P - Кортеж параметров функции мемоизации
 */
interface MemoizationOptions<T, P extends unknown[]> {
    /**
     * Пользовательская функция равенства для сравнения ключей вызова.
     * У фабрики с одним параметром сравниваются сами параметры, у фабрики с несколькими — кортежи
     * аргументов целиком. Это позволяет попадать в кеш для семантически эквивалентных ключей.
     *
     * @param a - Первый ключ для сравнения
     * @param b - Второй ключ для сравнения
     * @returns `true`, если ключи считаются равными, `false` в противном случае
     *
     * @example
     * ```ts
     * const options = {
     *   equals: (a, b) => a.id === b.id
     * };
     * ```
     */
    equals?: (a: MemoizationKey<P>, b: MemoizationKey<P>) => boolean;

    /**
     * Функция, которая вызывается при получении значения из кеша.
     * Вызывается каждый раз, когда мемоизированная функция возвращает значение из кеша
     * вместо повторного вычисления, что позволяет отслеживать или выполнять побочные эффекты при попаданиях в кеш.
     *
     * @param value - Закешированное значение, которое было получено
     *
     * @example
     * ```ts
     * const options = {
     *   onCacheHit: (value) => console.log('Попадание в кеш для:', value)
     * };
     * ```
     */
    onCacheHit?: (value: T) => void;

    /**
     * Сколько последних использованных значений удерживать сильными ссылками.
     * Кеш хранит значения через `WeakRef`, поэтому без сильного удержания значение, на которое
     * вызывающий код не сохранил ссылку (например, при деструктуризации результата), собирает
     * сборщик мусора — и мемоизация вырождается в вычисление на каждый вызов. Пин работает как LRU:
     * при переполнении вытесняется давно не запрашивавшийся ключ.
     *
     * Значение `0` выключает пин: значения держатся только через `WeakRef`. Это нужно лишь тогда,
     * когда нежелательно даже кратковременное сильное удержание значения — память при мемоизаторе
     * на каждый инстанс освобождает и сам `pinTtl`.
     *
     * Принимается конечное неотрицательное целое, иначе при создании фабрики бросается `RangeError`.
     *
     * @defaultValue 64
     *
     * @example
     * ```ts
     * // фабрика вызывается с одним-двумя параметрами — большой пин не нужен
     * const options = {
     *   maxPinned: 2
     * };
     * ```
     */
    maxPinned?: number;

    /**
     * Сколько миллисекунд запиненное значение держится сильной ссылкой без обращений, прежде чем
     * пин снимается сам и значение переходит на обычный путь — `WeakRef` + `FinalizationRegistry`.
     *
     * Пин без TTL опасен, когда сам `memoize(...)` создается на каждый инстанс (компонент, замыкание):
     * запиненное значение живо по построению, значит жив и его `WeakRef` в `refs`, значит `refs`
     * никогда не пустеет — и запись кеша остается в контексте навсегда, даже если инстанс давно
     * никому не нужен. TTL превращает это в конечную утечку: заброшенный инстанс освобождает память
     * сам, без обращения к кешу и без ручного `maxPinned: 0`.
     *
     * Каждое обращение к значению — и попадание в кеш, и первое вычисление — продвигает TTL заново,
     * то есть таймер меряет паузу между обращениями, а не суммарное время жизни значения. Значение,
     * которое запрашивают чаще TTL, из пина не вылетает никогда.
     *
     * На клиенте время двигают два независимых механизма: ленивая проверка при обращении к кешу
     * (дешево, но не сработает для инстанса, который больше никогда не вызовут) и один общий таймер
     * на запись кеша, который не держит запиненные значения сильной ссылкой сам (сравнивается только
     * временная метка) и поэтому не станет новым источником утечки — он тихо не переустанавливается,
     * если запись кеша или контекст успели исчезнуть.
     *
     * На сервере активного таймера нет и не нужно: кеш лежит в контексте запроса и умирает вместе
     * с ним, а таймер, запланированный внутри `AsyncLocalStorage`, наоборот удерживал бы весь
     * контекст запроса до своего срабатывания. Там остается только ленивая проверка.
     *
     * Принимается конечное положительное число миллисекунд либо `Infinity`, чтобы полностью выключить
     * TTL и вернуться к старому поведению «пин живет, пока жив контекст» — это осмысленно только для
     * мемоизаторов уровня модуля, которых конечное число. Любое другое значение бросает `RangeError`
     * при создании фабрики.
     *
     * @defaultValue 5 * 60 * 1000 (5 минут)
     *
     * @example
     * ```ts
     * // значения, которые обновляют раз в несколько секунд, не должны терять пин между обращениями
     * const options = {
     *   pinTtl: 30_000
     * };
     * ```
     */
    pinTtl?: number;
}

/**
 * Значение, которое сравнивает `equals`: единственный параметр для обратной совместимости либо
 * весь кортеж параметров для фабрики другой арности.
 */
type MemoizationKey<P extends unknown[]> = P extends [unknown?, ...never[]] ? P[0] : P;

/** Метка составного ключа, недоступная пользовательским значениям параметров */
const argumentsKeyTag = Symbol('argumentsKey');

/** Канонический ключ вызова с двумя и более параметрами */
interface ArgumentsKey<P extends unknown[]> {
    [argumentsKeyTag]: true;
    args: P;
}

/** Узел позиционного дерева ключей */
interface KeyTrieNode<K> {
    children: Map<unknown, KeyTrieNode<K>>;
    key?: K;
}

const createKeyTrieNode = <K>(): KeyTrieNode<K> => ({ children: new Map() });

/** Убирает составной ключ из позиционного дерева и подчищает опустевшие ветки */
function deleteExactKey<K, T extends object>(entry: FactoryCache<K, T>, key: K) {
    if (!isArgumentsKey(key)) {
        return;
    }

    const path: Array<[KeyTrieNode<K>, unknown, KeyTrieNode<K>]> = [];
    let node = entry.exactKeys;

    for (const arg of key.args) {
        const child = node.children.get(arg);
        if (!child) {
            return;
        }

        path.push([node, arg, child]);
        node = child;
    }

    if (node.key !== key) {
        return;
    }

    node.key = undefined;

    for (let index = path.length - 1; index >= 0; index--) {
        const segment = path[index];
        if (!segment) {
            break;
        }
        const [parent, arg, child] = segment;
        if (child.key !== undefined || child.children.size !== 0) {
            break;
        }
        parent.children.delete(arg);
    }
}

function isArgumentsKey(value: unknown): value is ArgumentsKey<unknown[]> {
    return typeof value === 'object' && value !== null && argumentsKeyTag in value;
}

/** Запиненное значение вместе с моментом времени, после которого пин снимается */
interface PinnedEntry<T> {
    value: T;
    /** `Date.now()`, после которого запись считается просроченной; `Infinity`, если TTL выключен */
    expiresAt: number;
}

/**
 * Held value финалайзера значения.
 *
 * Хранилище и запись кеша держим слабо: `FinalizationRegistry` не отпускает held value, пока жива
 * цель, а сильная ссылка на запись достала бы через ее `pinned` до самой цели — значение стало бы
 * неубиваемым.
 */
interface FinalizationHoldings<K, T extends object> {
    storageRef: WeakRef<Map<symbol, FactoryCache<unknown, object>>>;
    entryRef: WeakRef<FactoryCache<K, T>>;
    /** Ключ записи кеша в хранилище контекста */
    cacheKey: symbol;
    key: K;
    ref: WeakRef<T>;
}

/**
 * Убирает следы собранного GC значения: слабую ссылку из записи кеша, а опустевшую запись —
 * из хранилища контекста.
 *
 * Объявлен на уровне модуля, а не в замыкании `memoize`: `FinalizationRegistry` живет в самой записи
 * кеша, поэтому колбэк из замыкания удерживал бы через нее мемоизированную функцию вместе со всем,
 * что она замкнула, — например, инстанс, на который ее создали.
 */
function finalizeValue<K, T extends object>({ storageRef, entryRef, cacheKey, key, ref }: FinalizationHoldings<K, T>) {
    // Если чистить уже нечего (запрос закончился), ref пустой
    const entry = entryRef.deref();
    if (!entry) {
        return;
    }

    // Удаляем запись только если под этим ключом всё ещё лежит именно тот `WeakRef`, значение
    // которого собрал GC. Между сбором значения и запуском этого финалайзера тот же ключ мог быть
    // перезаписан новым живым значением — его трогать нельзя.
    if (entry.refs.get(key) === ref) {
        entry.refs.delete(key);
        deleteExactKey(entry, key);
    }

    const storage = storageRef.deref();
    // `pinned` проверять не нужно: запиненное значение живо, значит жив и его `WeakRef`,
    // значит его запись в `refs` не удалена и `refs` не пуст.
    if (entry.refs.size === 0 && storage?.get(cacheKey) === entry) {
        storage.delete(cacheKey);
    }
}

/**
 * Кеш одной мемоизированной функции.
 *
 * @typeParam K - Тип ключа (параметра функции мемоизации)
 * @typeParam T - Тип закешированного значения
 */
interface FactoryCache<K, T extends object> {
    /** Слабые ссылки на все живые значения */
    refs: Map<K, WeakRef<T>>;

    /** Позиционное дерево канонических составных ключей для поиска за O(число аргументов) */
    exactKeys: KeyTrieNode<K>;

    /**
     * Сильные ссылки на последние использованные значения; порядок вставки в Map = порядок LRU,
     * а благодаря монотонному TTL это же и порядок возрастания `expiresAt` — самая старая (и первой
     * истекающая) запись всегда лежит первой при переборе.
     */
    pinned: Map<K, PinnedEntry<T>>;

    /**
     * Финалайзер значений этой записи. Живет в записи, а не в замыкании `memoize`: замыкание умирает
     * вместе с мемоизатором, созданным на инстанс, и собралось бы тем же циклом GC, что и значение, —
     * колбэк бы не выполнился. Held values слабые, поэтому нового удержания это не создает.
     */
    registry: FinalizationRegistry<FinalizationHoldings<K, T>>;

    /**
     * Слабая ссылка на саму запись — одна на запись, ее переиспользует таймер чистки. Создание
     * `WeakRef` кладет цель в KeptAlive до конца текущей задачи, а запись держит `pinned` сильно,
     * поэтому лишний `WeakRef` на каждое обращение продлевал бы жизнь запиненным значениям.
     */
    selfRef?: WeakRef<FactoryCache<K, T>>;

    /** Таймер общей активной чистки просроченного пина; только на клиенте, см. `scheduleSweep` */
    sweepTimer?: ReturnType<typeof setTimeout>;
}

/** Размер сильного пина по умолчанию */
const DEFAULT_MAX_PINNED = 64;

/** TTL пина по умолчанию: достаточно, чтобы не мешать горячим обращениям, и достаточно мало, чтобы заброшенный за-компонентный инстанс освободил память в разумное время */
const DEFAULT_PIN_TTL_MS = 5 * 60 * 1000;

/**
 * Предел задержки `setTimeout` (знаковый int32). Большая задержка переполняется, и таймер срабатывает
 * почти сразу — и в Node, и в браузерных движках.
 */
const MAX_TIMEOUT_DELAY_MS = 2_147_483_647;

/**
 * Хранилище закешированных значений для каждого запроса.
 * Обеспечивает контекстно-зависимое кеширование, которое изолирует кеши мемоизации между различными контекстами выполнения.
 */
const { getContext, resetContext: resetMemoizeByParamCache } = createContext<
    Map<symbol, FactoryCache<unknown, object>>
>({
    name: '@adv-frontend/modules-memo/memoize',
    getInitialValue: () => new Map(),
});

export { resetMemoizeByParamCache };

/**
 * Создает мемоизированную версию функции, которая кеширует результаты.
 *
 * Ключ записи — кортеж параметров вызова. Без `options.equals` составные ключи ищутся в позиционном
 * дереве `Map` по правилам `SameValueZero` за O(число параметров). С `options.equals` сравнивается
 * весь кортеж; для функции с одним параметром `equals` по-прежнему получает сам параметр. У каждой
 * мемоизированной функции свой кеш. Кеш лежит в контексте запроса `modules-context`, а не
 * в замыкании: на сервере у каждого `runWithContext` свой кеш и значения не переживают запрос,
 * на клиенте контекст один на все приложение.
 *
 * Хранение гибридное. Все значения кеш держит через `WeakRef` — так он самоочищается, даже когда
 * кардинальность параметров не ограничена (например, объект параметров создается заново на каждый
 * рендер, а вкладка живет неделями). Вдобавок последние `options.maxPinned` использованных значений
 * удерживаются сильными ссылками, чтобы кеш не вымывался, когда вызывающий код не хранит ссылку на
 * результат — как при `const { data } = memoized()`. Пин работает как LRU по порядку обращений и
 * автоматически снимается через `options.pinTtl` миллисекунд простоя, поэтому мемоизатор, случайно
 * созданный на каждый инстанс/рендер вместо уровня модуля, освобождает память сам — без ручного
 * `maxPinned: 0` и без единого обращения к брошенному инстансу.
 *
 * Значение вычисляется заново, если параметр не совпал, если предыдущее значение вытеснено из пина
 * (переполнением `maxPinned` или истечением `pinTtl`) и собрано сборщиком мусора, или если кеш
 * сброшен через `resetMemoizeCache`.
 *
 * За пределами пина значение живет ровно столько, сколько его удерживают потребители, поэтому
 * мемоизировать стоит сам объект состояния, а не временную обертку над ним, из которой наружу
 * отдают только поля: такую обертку никто не удержит, и вытесненная запись пересоздаст состояние
 * (DIRECT-327483). Сопутствующие данные держите в `WeakMap` с ключом по этому объекту.
 *
 * @typeParam T - Тип возвращаемого значения функции (должен быть объектом)
 * @typeParam P - Кортеж параметров функции
 * @param fn - Функция для мемоизации
 * @param options - Опциональная конфигурация
 * @returns Мемоизированная версия входной функции
 *
 * @example
 * ```ts
 * const memoized = memoize(() => ({ data: 'value' }));
 * const result1 = memoized();
 * const result2 = memoized(); // Возвращает кешированный результат
 * ```
 *
 * @example
 * ```ts
 * const memoized = memoize(
 *   (id: number) => ({ id, data: 'value' }),
 *   { equals: (a, b) => a === b }
 * );
 * ```
 */
export function memoize<T extends object, P extends unknown[]>(
    fn: (...args: P) => T,
    options?: MemoizationOptions<T, P>
): (...args: P) => T {
    type Key = P[number] | undefined | symbol | ArgumentsKey<P>;

    const cacheKey = Symbol();
    const noArgsKey = Symbol('noArgsKey');
    const notFound = Symbol();
    const maxPinned = options?.maxPinned ?? DEFAULT_MAX_PINNED;
    const pinTtl = options?.pinTtl ?? DEFAULT_PIN_TTL_MS;

    // При `NaN` или `Infinity` пин перестает вытеснять и удерживает все значения до конца жизни контекста
    if (!Number.isInteger(maxPinned) || maxPinned < 0) {
        throw new RangeError(
            `memoize: maxPinned должен быть конечным неотрицательным целым числом, получено ${String(maxPinned)}`
        );
    }

    // `Infinity` разрешен осознанно — это единственный способ вернуться к пину без TTL.
    // `Number.isFinite` заодно отсекает нечисловые значения: строка `'100'` прошла бы `<= 0`,
    // а потом молча включила бы вечный пин — ровно ту утечку, которую закрывает эта опция.
    if (pinTtl !== Infinity && (!Number.isFinite(pinTtl) || pinTtl <= 0)) {
        throw new RangeError(
            `memoize: pinTtl должен быть положительным числом миллисекунд либо Infinity, получено ${String(pinTtl)}`
        );
    }

    /** Пустая запись кеша вместе с собственным финалайзером; см. `FactoryCache.registry` */
    const createEntry = (): FactoryCache<Key, T> => ({
        refs: new Map(),
        exactKeys: createKeyTrieNode(),
        pinned: new Map(),
        registry: new FinalizationRegistry<FinalizationHoldings<Key, T>>(finalizeValue),
    });

    /**
     * Ищет ключ, равный аргументам вызова по `options.equals`.
     *
     * Сначала просматриваются запиненные ключи: их не больше `maxPinned`, и это последние
     * использованные параметры — самый частый случай повторного вызова. Полный скан всего
     * кеша идет только при промахе.
     */
    const findExactKey = (entry: FactoryCache<Key, T>, args: P): Key => {
        if (args.length === 0) {
            return noArgsKey;
        }
        if (args.length === 1) {
            return args[0];
        }

        let node = entry.exactKeys;
        for (const arg of args) {
            const child = node.children.get(arg);
            if (!child) {
                return notFound;
            }
            node = child;
        }

        return node.key ?? notFound;
    };

    const getComparableKey = (key: Key): MemoizationKey<P> => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- внутренние ключи соответствуют арности P
        return (isArgumentsKey(key) ? key.args : key) as MemoizationKey<P>;
    };

    const getKeyArity = (key: Key) => {
        if (key === noArgsKey) {
            return 0;
        }

        return isArgumentsKey(key) ? key.args.length : 1;
    };

    /** Ищет канонический ключ вызова, в том числе по пользовательскому сравнению всего кортежа */
    const findKey = (entry: FactoryCache<Key, T>, args: P): Key => {
        if (!options?.equals || args.length === 0) {
            return findExactKey(entry, args);
        }

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- для одного параметра сравнивается он сам, для нескольких — весь кортеж
        const comparableArgs = (args.length === 1 ? args[0] : args) as MemoizationKey<P>;

        for (const [key] of entry.pinned) {
            if (getKeyArity(key) === args.length && options.equals(comparableArgs, getComparableKey(key))) {
                return key;
            }
        }

        for (const [key] of entry.refs) {
            // запиненные ключи уже проверены на первом проходе
            if (entry.pinned.has(key)) {
                continue;
            }

            if (getKeyArity(key) === args.length && options.equals(comparableArgs, getComparableKey(key))) {
                return key;
            }
        }

        return notFound;
    };

    /** Создает канонический ключ промахнувшегося вызова и добавляет составной ключ в дерево */
    const createKey = (entry: FactoryCache<Key, T>, args: P): Key => {
        if (args.length === 0) {
            return noArgsKey;
        }
        if (args.length === 1) {
            return args[0];
        }

        const key: ArgumentsKey<P> = { [argumentsKeyTag]: true, args };

        if (!options?.equals) {
            let node = entry.exactKeys;
            for (const arg of args) {
                let child = node.children.get(arg);
                if (!child) {
                    child = createKeyTrieNode();
                    node.children.set(arg, child);
                }
                node = child;
            }
            node.key = key;
        }

        return key;
    };

    /**
     * Снимает пин с просроченных записей. Не трогает `refs` и не удаляет запись из контекста —
     * это по-прежнему делает только `FinalizationRegistry`, после того как GC соберет значение,
     * которое теперь удерживается лишь через `WeakRef`.
     *
     * Порядок вставки в `pinned` монотонен по `expiresAt` (см. `touchPin`), поэтому просроченные
     * записи — это всегда префикс перебора; на первой еще живой записи можно остановиться.
     */
    const sweepExpiredPins = (entry: FactoryCache<Key, T>, now: number) => {
        for (const [key, pinnedEntry] of entry.pinned) {
            if (pinnedEntry.expiresAt > now) {
                break;
            }

            entry.pinned.delete(key);
        }
    };

    /** Единственная слабая ссылка на запись; см. `FactoryCache.selfRef` */
    const getSelfRef = (entry: FactoryCache<Key, T>): WeakRef<FactoryCache<Key, T>> => {
        entry.selfRef ??= new WeakRef(entry);

        return entry.selfRef;
    };

    /**
     * Общий таймер на запись кеша, а не на значение: замыкание таймера держит запись и хранилище
     * только через `WeakRef`, поэтому само по себе оно ничего не удерживает и не станет новым
     * источником утечки. Если запись успела исчезнуть (GC собрал ее целиком, контекст сброшен),
     * таймер тихо не переустанавливается — досрочно поднимать его незачем, для этого есть ленивая
     * проверка при следующем реальном обращении к кешу.
     *
     * Это и есть путь освобождения памяти для заброшенного инстанса: без единого вызова мемоизатора
     * таймер сам снимет просроченный пин, значение станет достижимо только через `WeakRef`, GC его
     * соберет, `FinalizationRegistry` уберет запись из `refs`, а опустевшую запись — из контекста.
     *
     * Планируется только на клиенте — см. гард в `touchPin`.
     *
     * Задержка обрезается до предела `setTimeout`: остаток длинного TTL доигрывает сам колбэк, потому
     * что пересчитывает задержку от `expiresAt` еще живой записи пина.
     */
    const scheduleSweep = (entryRef: WeakRef<FactoryCache<Key, T>>, delayMs: number) => {
        const clampedDelayMs = Math.min(delayMs, MAX_TIMEOUT_DELAY_MS);

        const timer = setTimeout(() => {
            const entry = entryRef.deref();
            if (!entry) {
                return;
            }

            entry.sweepTimer = undefined;
            sweepExpiredPins(entry, Date.now());

            const oldest = entry.pinned.values().next().value;
            if (oldest) {
                entry.sweepTimer = scheduleSweep(entryRef, Math.max(0, oldest.expiresAt - Date.now()));
            }
        }, clampedDelayMs);

        // Тип возврата `setTimeout` неоднозначен, когда в `lib` одновременно `dom` и `@types/node`
        // (в браузере — число, под Node — `NodeJS.Timeout`), поэтому проверяем `unref` в рантайме.
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. комментарий выше про двойное разрешение типа
        const maybeNodeTimer = timer as unknown as { unref?: () => void };
        if (typeof maybeNodeTimer.unref === 'function') {
            // Клиентский код исполняется под Node в тестах и в SSR-сборках дев-инструментов —
            // там таймер не должен сам по себе держать процесс живым до истечения `pinTtl`
            maybeNodeTimer.unref();
        }

        return timer;
    };

    // LRU по порядку вставки в `Map`: переставляем ключ в конец и вытесняем самый давний.
    // Тем же движением продвигаем TTL: каждое обращение — попадание в кеш или первое
    // вычисление — откладывает истечение пина заново, поэтому значение, которое запрашивают
    // чаще `pinTtl`, из пина не вылетает.
    const touchPin = (entry: FactoryCache<Key, T>, key: Key, value: T, now: number) => {
        const { pinned } = entry;
        pinned.delete(key);
        pinned.set(key, { value, expiresAt: Number.isFinite(pinTtl) ? now + pinTtl : Infinity });

        if (pinned.size > maxPinned) {
            // при `maxPinned = 0` сразу удаляется только что добавленный ключ — пин выключен
            const oldest = pinned.keys().next().value;
            pinned.delete(oldest);
        }

        // Активная чистка нужна только на клиенте: там контекст живет всю вкладку, и без таймера
        // брошенный инстанс никогда не освободит пин. На сервере кеш умирает вместе с запросом,
        // а таймер, запланированный внутри `AsyncLocalStorage`, наоборот удерживал бы весь контекст
        // запроса до срабатывания — `unref` от этого не спасает.
        //
        // Один таймер на запись: следующий уже запланирован сработать не позже самой старой
        // (значит первой истекающей) записи и сам себя переустановит на новую самую старую запись
        if (process.env.BUILD_TARGET === 'client' && Number.isFinite(pinTtl) && entry.sweepTimer === undefined) {
            const next = pinned.values().next().value;
            if (next) {
                entry.sweepTimer = scheduleSweep(getSelfRef(entry), Math.max(0, next.expiresAt - now));
            }
        }
    };

    return (...args: P) => {
        let value: T | undefined;

        const now = Date.now();
        const storage = getContext();

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- контекст не знает о типах экземпляров
        let entry = storage.get(cacheKey) as FactoryCache<Key, T> | undefined;
        if (!entry) {
            entry = createEntry();
            storage.set(cacheKey, entry);
        }

        // Ленивая часть TTL: снимаем пин с того, что успело истечь, до поиска ключа. На результат
        // поиска это не влияет — значение остается в `refs`, пока его не собрал GC, — а лишь заранее
        // выполняет то, что рано или поздно все равно сделает общий таймер записи.
        sweepExpiredPins(entry, now);

        const key = findKey(entry, args);
        const ref = entry.refs.get(key);
        value = ref?.deref();

        if (ref && !value) {
            // в `pinned` такого ключа быть не может: запиненное значение живо по построению
            entry.refs.delete(key);
        }

        if (value) {
            touchPin(entry, key, value, now);
            options?.onCacheHit?.(value);
            return value;
        }

        value = fn(...args);
        const newKey = createKey(entry, args);
        const newRef = new WeakRef(value);
        entry.refs.set(newKey, newRef);
        entry.registry.register(value, {
            storageRef: new WeakRef(storage),
            entryRef: getSelfRef(entry),
            cacheKey,
            key: newKey,
            ref: newRef,
        });
        touchPin(entry, newKey, value, now);

        return value;
    };
}
